import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Calendar,
  Clock,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Users,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
} from "lucide-react";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useAuth } from "../../hooks/useAuth";
import {
  getClasses,
  addPeriodToSchedule,
  updatePeriodInSchedule,
  deletePeriodFromSchedule,
} from "../../services/classes";
import { getCourses } from "../../services/courses";
import { getTeachers } from "../../services/teachers";
import { getStudents } from "../../services/students";

// Types
interface SchedulePeriod {
  _id?: string;
  day:
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";
  startTime: string;
  endTime: string;
  subjectName: string;
  teacherId?: string;
  room?: string;
  /** Empty = all students; non-empty = only these students attend */
  studentIds?: string[];
  /** Local UI helper — the _id of the selected course (not sent separately to API) */
  subjectId?: string;
}

interface ClassSchedule {
  _id: string;
  name: string;
  grade: string;
  section: string;
  schedule?: {
    periods: SchedulePeriod[];
  };
}

const DAYS_OF_WEEK: SchedulePeriod["day"][] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const TIME_SLOTS = [
  "08:00",
  "08:30",
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
];

// Helper function to check if a time slot is within a period's duration
const SecretarySchedule: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [editingPeriod, setEditingPeriod] = useState<SchedulePeriod | null>(
    null,
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDay, setSelectedDay] = useState<string>("all");
  const [studentSearchTerm, setStudentSearchTerm] = useState("");

  // Form state for new/edit period
  const [periodForm, setPeriodForm] = useState<Partial<SchedulePeriod>>({
    day: "monday",
    startTime: "09:00",
    endTime: "10:00",
    subjectName: "",
    subjectId: "", // course _id — used to query enrolled students
    teacherId: "",
    room: "",
    studentIds: [],
  });

  // Fetch classes
  const {
    data: classesData,
    isLoading: classesLoading,
    error: classesError,
  } = useQuery({
    queryKey: ["classes"],
    queryFn: () => getClasses({ page: 1, limit: 100 }),
  });

  // Fetch courses
  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ["courses"],
    queryFn: () => getCourses({ page: 1, limit: 100 }),
  });

  // Fetch teachers
  const { data: teachersData, isLoading: teachersLoading } = useQuery({
    queryKey: ["teachers"],
    queryFn: () => getTeachers({ page: 1, limit: 100 }),
  });

  // Fetch students enrolled in the currently selected course (updates when course changes)
  const { data: courseStudentsData, isLoading: courseStudentsLoading } =
    useQuery({
      queryKey: ["course-students", periodForm.subjectId],
      queryFn: () =>
        getStudents({ courseId: periodForm.subjectId!, limit: 500 }),
      enabled: !!periodForm.subjectId,
    });

  const classes = classesData?.data || [];
  const courses = coursesData?.data || [];
  const teachers = teachersData?.data || [];
  const courseStudents = courseStudentsData?.data || [];

  const normalizedStudentSearch = studentSearchTerm.trim().toLowerCase();
  const filteredCourseStudents = courseStudents.filter((student: any) => {
    if (!normalizedStudentSearch) return true;

    const fullName =
      `${student.userInfo?.firstName || ""} ${student.userInfo?.lastName || ""}`
        .trim()
        .toLowerCase();
    const admissionNumber = String(
      student.admissionNumber || student.studentId || "",
    ).toLowerCase();

    return (
      fullName.includes(normalizedStudentSearch) ||
      admissionNumber.includes(normalizedStudentSearch)
    );
  });

  // Get selected class details
  const currentClass = classes.find((cls: any) => cls._id === selectedClass);
  const periods: SchedulePeriod[] =
    (currentClass?.schedule as any)?.periods || [];

  // Filter periods by day
  const filteredPeriods =
    selectedDay === "all"
      ? periods
      : periods.filter((period: SchedulePeriod) => period.day === selectedDay);

  // Group periods by day for calendar view
  const periodsByDay = DAYS_OF_WEEK.reduce(
    (acc, day) => {
      acc[day] = periods
        .filter((period: SchedulePeriod) => period.day === day)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
      return acc;
    },
    {} as Record<string, SchedulePeriod[]>,
  );

  // Add period mutation
  const addPeriodMutation = useMutation({
    mutationFn: async (data: SchedulePeriod) => {
      if (!selectedClass) throw new Error("No class selected");
      return addPeriodToSchedule(selectedClass, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["classes"] });
      setShowAddModal(false);
      resetForm();
    },
    onError: (error: any) => {
      console.error("Error adding period:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add period";
      toast.error(message, { duration: 6000 });
    },
  });

  // Update period mutation
  const updatePeriodMutation = useMutation({
    mutationFn: async (data: SchedulePeriod) => {
      if (!selectedClass || !editingPeriod?._id)
        throw new Error("Missing class or period id");
      return updatePeriodInSchedule(selectedClass, editingPeriod._id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["classes"] });
      setShowAddModal(false);
      resetForm();
    },
    onError: (error: any) => {
      console.error("Error updating period:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update period";
      toast.error(message, { duration: 6000 });
    },
  });

  // Delete period mutation
  const deletePeriodMutation = useMutation({
    mutationFn: async ({
      classId,
      periodId,
    }: {
      classId: string;
      periodId: string;
    }) => {
      return deletePeriodFromSchedule(classId, periodId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },
    onError: (error: any) => {
      console.error("Error deleting period:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete period";
      toast.error(message);
    },
  });

  // Reset form
  const resetForm = () => {
    setPeriodForm({
      day: "monday" as const,
      startTime: "09:00",
      endTime: "10:00",
      subjectName: "",
      subjectId: "",
      teacherId: "",
      room: "",
      studentIds: [],
    });
    setStudentSearchTerm("");
    setEditingPeriod(null);
  };

  // Toggle a student in/out of the studentIds selection
  const toggleStudentId = (studentId: string) => {
    const current = periodForm.studentIds || [];
    const updated = current.includes(studentId)
      ? current.filter((id) => id !== studentId)
      : [...current, studentId];
    setPeriodForm({ ...periodForm, studentIds: updated });
  };

  // Handle delete period
  const handleDeletePeriod = (periodId: string) => {
    if (!selectedClass) return;

    if (window.confirm("Are you sure you want to delete this period?")) {
      deletePeriodMutation.mutate({
        classId: selectedClass,
        periodId,
      });
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !selectedClass ||
      !periodForm.subjectName ||
      !periodForm.startTime ||
      !periodForm.endTime
    )
      return;

    if (editingPeriod?._id) {
      updatePeriodMutation.mutate(periodForm as SchedulePeriod);
    } else {
      addPeriodMutation.mutate(periodForm as SchedulePeriod);
    }
  };

  // Get teacher name
  const getTeacherName = (teacherId?: string) => {
    if (!teacherId) return "Not assigned";
    const teacher = teachers.find((t: any) => t._id === teacherId);
    return teacher
      ? `${teacher.userInfo?.firstName || ""} ${
          teacher.userInfo?.lastName || ""
        }`.trim()
      : "Unknown";
  };

  // Get subject color with gradient
  const getSubjectColor = (subject: string) => {
    const colors = [
      "bg-gradient-to-br from-blue-300 to-indigo-400 text-white font-bold shadow-sm",
      "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white font-bold shadow-sm",
      "bg-gradient-to-br from-purple-300 to-purple-400 text-white font-bold shadow-sm",
      "bg-gradient-to-br from-amber-300 to-yellow-400 text-white font-bold shadow-sm",
      "bg-gradient-to-br from-pink-300 to-pink-400 text-white font-bold shadow-sm",
      "bg-gradient-to-br from-indigo-300 to-indigo-400 text-white font-bold shadow-sm",
    ];
    return colors[subject.length % colors.length];
  };

  if (classesLoading || coursesLoading || teachersLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
          <p className="mt-4 text-slate-600 font-medium">
            Loading schedules...
          </p>
        </div>
      </div>
    );
  }

  if (classesError) {
    return (
      <div className="p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
        <div className="bg-white/95 backdrop-blur-xl border border-red-200/60 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center shadow-md">
              <X className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-red-800 font-bold">Error Loading Data</h3>
              <p className="text-red-600 mt-1 font-medium">
                Unable to load classes data. Please try refreshing the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 lg:mb-8 bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-4 lg:p-6 border border-slate-200/60">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          Class Scheduling
        </h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-base font-medium">
          Manage class schedules and time tables
        </p>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 lg:mb-6">
        {/* Class Selection */}
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2">
            Select Class
          </label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-all duration-300 font-semibold text-slate-700 bg-white"
          >
            <option value="">Choose a class...</option>
            {classes.map((cls: any) => (
              <option key={cls._id} value={cls._id}>
                {cls.name} - {cls.section}
              </option>
            ))}
          </select>
        </div>

        {/* Day Filter */}
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2">
            Filter by Day
          </label>
          <select
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-all duration-300 font-semibold text-slate-700 bg-white"
          >
            <option value="all">All Days</option>
            {DAYS_OF_WEEK.map((day) => (
              <option key={day} value={day}>
                {day.charAt(0).toUpperCase() + day.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Add Period Button */}
        {/* Add Period Button */}
        <div className="sm:col-span-2 lg:col-span-2 flex items-end">
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            disabled={!selectedClass}
            className={`group relative px-6 py-2.5 rounded-xl font-bold text-white shadow-lg transition-all duration-300 w-full sm:w-auto ${
              !selectedClass
                ? "bg-slate-300 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-300 to-blue-400 hover:shadow-xl hover:scale-105"
            }`}
          >
            <Plus className="w-5 h-5 mr-2 inline-block" />
            Add Period
          </button>
        </div>
      </div>

      {selectedClass ? (
        <div className="space-y-6">
          {/* Class Info */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {currentClass?.name} - {currentClass?.section}
                </h2>
                <p className="text-slate-600 font-semibold mt-1">
                  Grade: {currentClass?.grade} | Total Periods: {periods.length}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center shadow-md">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-bold text-slate-700">
                  {currentClass?.students?.length || 0} Students
                </span>
              </div>
            </div>
          </div>

          {/* Weekly Schedule Calendar */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 overflow-hidden">
            <div className="p-3 sm:p-4 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
              <h3 className="text-lg font-bold text-slate-900">
                Weekly Schedule
              </h3>
              <p className="text-sm text-slate-600 mt-1 font-medium">
                Scroll horizontally to view all days
              </p>
            </div>

            <div className="overflow-x-auto">
              <div className="grid grid-cols-8 min-w-[800px]">
                {/* Ensures minimum width for better readability */}
                {/* Time column header */}
                <div className="p-2 sm:p-3 border-r border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50 font-bold text-xs sm:text-sm text-slate-700">
                  Time
                </div>

                {/* Day headers */}
                {DAYS_OF_WEEK.map((day) => (
                  <div
                    key={day}
                    className="p-2 sm:p-3 border-r border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50 font-bold text-xs sm:text-sm text-slate-700 text-center"
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </div>
                ))}

                {/* Time slots and periods */}
                {TIME_SLOTS.map((timeSlot) => (
                  <React.Fragment key={timeSlot}>
                    {/* Time slot */}
                    <div className="p-1 sm:p-2 border-r border-b border-slate-200/60 bg-slate-50/50 text-xs text-slate-600 font-semibold">
                      {timeSlot}
                    </div>

                    {/* Day cells */}
                    {DAYS_OF_WEEK.map((day) => {
                      const dayPeriods = periodsByDay[day] || [];

                      // Find period that starts at this time slot
                      const startingPeriod = dayPeriods.find(
                        (p) => p.startTime === timeSlot,
                      );

                      return (
                        <div
                          key={`${day}-${timeSlot}`}
                          className="p-1 border-r border-b border-slate-200/60 min-h-[60px] relative overflow-hidden hover:bg-blue-50/30 transition-colors"
                        >
                          {startingPeriod && (
                            <div
                              title={[
                                startingPeriod.subjectName,
                                `${startingPeriod.startTime} – ${startingPeriod.endTime}`,
                                startingPeriod.room
                                  ? `Room: ${startingPeriod.room}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join("\n")}
                              className={`p-2 rounded-lg text-xs h-[calc(100%-8px)] overflow-hidden cursor-default ${getSubjectColor(
                                startingPeriod.subjectName,
                              )}`}
                              style={{
                                position: "absolute",
                                top: "4px",
                                left: "4px",
                                right: "4px",
                                zIndex: 10,
                              }}
                            >
                              <div className="font-bold truncate">
                                {startingPeriod.subjectName}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Periods List */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60">
            <div className="p-4 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
              <h3 className="text-lg font-bold text-slate-900">
                Schedule Periods
              </h3>
            </div>

            <div className="p-4">
              {filteredPeriods.length > 0 ? (
                <div className="space-y-3">
                  {filteredPeriods.map((period: SchedulePeriod) => (
                    <div
                      key={period._id || `${period.day}-${period.startTime}`}
                      className="flex items-center justify-between p-4 border border-slate-200/60 rounded-xl hover:shadow-md hover:scale-[1.02] transition-all duration-300 bg-white"
                    >
                      <div className="flex items-center space-x-4">
                        <div
                          className={`px-3 py-1 rounded-xl text-sm ${getSubjectColor(
                            period.subjectName,
                          )}`}
                        >
                          {period.day.charAt(0).toUpperCase() +
                            period.day.slice(1)}
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900">
                            {period.subjectName}
                          </h4>
                          <p className="text-sm text-slate-600 font-semibold">
                            {period.startTime} - {period.endTime}
                            {period.room && ` | Room: ${period.room}`}
                          </p>
                          <p className="text-xs text-slate-500 font-medium">
                            Teacher: {getTeacherName(period.teacherId)}
                          </p>
                          <p className="text-xs font-medium mt-0.5">
                            {period.studentIds &&
                            period.studentIds.length > 0 ? (
                              <span className="text-indigo-600">
                                {period.studentIds.length} specific student(s)
                              </span>
                            ) : (
                              <span className="text-emerald-600">
                                All class students
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            // Resolve subjectId from the courses list so the
                            // course dropdown and student panel populate correctly
                            const matchingCourse = courses.find(
                              (c: any) => c.name === period.subjectName,
                            );
                            setEditingPeriod(period);
                            setPeriodForm({
                              ...period,
                              subjectId: matchingCourse?._id || "",
                            });
                            setShowAddModal(true);
                          }}
                          className="p-2 rounded-lg bg-gradient-to-br from-blue-300 to-blue-400 text-white hover:shadow-md hover:scale-110 transition-all duration-300"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            handleDeletePeriod(period._id!);
                          }}
                          className="p-2 rounded-lg bg-gradient-to-br from-red-300 to-pink-400 text-white hover:shadow-md hover:scale-110 transition-all duration-300"
                          disabled={deletePeriodMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center mx-auto mb-4 shadow-md">
                    <Calendar className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    No periods scheduled
                  </h3>
                  <p className="text-slate-600 mb-4 font-medium">
                    {selectedDay === "all"
                      ? "This class doesn't have any periods scheduled yet."
                      : `No periods scheduled for ${selectedDay}.`}
                  </p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-6 py-2.5 bg-gradient-to-r from-blue-300 to-blue-400 text-white font-bold rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    Add First Period
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto mb-4 shadow-md">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              Select a Class
            </h3>
            <p className="text-slate-600 font-medium">
              Choose a class from the dropdown above to view and manage its
              schedule.
            </p>
          </div>
        </div>
      )}

      {/* Add/Edit Period Modal — full screen */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-6 py-4 bg-white/95 backdrop-blur-xl border-b border-slate-200/60 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center shadow-md">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  {editingPeriod ? "Edit Period" : "Add New Period"}
                </h3>
                <p className="text-sm text-slate-500 font-medium">
                  Fill in the details below to schedule a class period
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowAddModal(false);
                resetForm();
              }}
              className="p-2 hover:bg-slate-200/50 rounded-xl transition-all duration-300 hover:scale-110"
            >
              <X className="h-6 w-6 text-slate-500" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* LEFT COLUMN */}
                <div className="space-y-5">
                  {/* Schedule timing */}
                  <div className="bg-white/95 rounded-2xl shadow-sm border border-slate-200/60 p-5">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">
                      Schedule
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                          Day
                        </label>
                        <select
                          value={periodForm.day}
                          onChange={(e) =>
                            setPeriodForm({
                              ...periodForm,
                              day: e.target.value as SchedulePeriod["day"],
                            })
                          }
                          className="w-full px-3 py-2.5 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-700 font-semibold shadow-sm transition-all"
                          required
                        >
                          {DAYS_OF_WEEK.map((day) => (
                            <option key={day} value={day}>
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-2">
                            Start Time
                          </label>
                          <select
                            value={periodForm.startTime}
                            onChange={(e) =>
                              setPeriodForm({
                                ...periodForm,
                                startTime: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2.5 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-700 font-semibold shadow-sm transition-all"
                            required
                          >
                            {TIME_SLOTS.map((time) => (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-2">
                            End Time
                          </label>
                          <select
                            value={periodForm.endTime}
                            onChange={(e) =>
                              setPeriodForm({
                                ...periodForm,
                                endTime: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2.5 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-700 font-semibold shadow-sm transition-all"
                            required
                          >
                            {TIME_SLOTS.map((time) => (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subject, Teacher & Room */}
                  <div className="bg-white/95 rounded-2xl shadow-sm border border-slate-200/60 p-5">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">
                      Subject &amp; Teacher
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                          Subject / Course
                        </label>
                        <select
                          value={periodForm.subjectId}
                          onChange={(e) => {
                            const selected = courses.find(
                              (c: any) => c._id === e.target.value,
                            );
                            setPeriodForm({
                              ...periodForm,
                              subjectId: e.target.value,
                              subjectName: selected?.name || "",
                              studentIds: [],
                            });
                            setStudentSearchTerm("");
                          }}
                          className="w-full px-3 py-2.5 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-700 font-semibold shadow-sm transition-all"
                          required
                        >
                          <option value="">Select a course...</option>
                          {courses.map((course: any) => (
                            <option key={course._id} value={course._id}>
                              {course.name} ({course.level})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                          Teacher
                        </label>
                        <select
                          value={periodForm.teacherId}
                          onChange={(e) =>
                            setPeriodForm({
                              ...periodForm,
                              teacherId: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2.5 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-700 font-semibold shadow-sm transition-all"
                        >
                          <option value="">Select a teacher...</option>
                          {teachers.map((teacher: any) => (
                            <option key={teacher._id} value={teacher._id}>
                              {teacher.userInfo?.firstName}{" "}
                              {teacher.userInfo?.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                          Room (Optional)
                        </label>
                        <input
                          type="text"
                          value={periodForm.room || ""}
                          onChange={(e) =>
                            setPeriodForm({
                              ...periodForm,
                              room: e.target.value,
                            })
                          }
                          placeholder="e.g., Room 101, Lab A"
                          className="w-full px-3 py-2.5 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-700 font-semibold shadow-sm transition-all placeholder:text-slate-400"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN — Student Assignment */}
                <div className="bg-white/95 rounded-2xl shadow-sm border border-slate-200/60 p-5 flex flex-col">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">
                    Student Assignment
                  </h4>

                  {/* All students toggle */}
                  <label className="flex items-center gap-3 cursor-pointer mb-4 select-none p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded accent-emerald-500"
                      checked={!periodForm.studentIds?.length}
                      onChange={() =>
                        setPeriodForm({ ...periodForm, studentIds: [] })
                      }
                    />
                    <div>
                      <span className="text-sm font-bold text-slate-700 block">
                        All enrolled students
                      </span>
                      <span className="text-xs text-slate-400">
                        Default — every student enrolled in the course attends
                      </span>
                    </div>
                  </label>

                  {/* Per-student checkboxes */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden flex flex-col flex-1">
                    <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between shrink-0">
                      <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                        {periodForm.subjectId
                          ? "Students enrolled in this course"
                          : "Select a course first"}
                      </span>
                      <div className="flex items-center gap-2">
                        {courseStudentsLoading && (
                          <span className="text-xs text-slate-400">
                            Loading…
                          </span>
                        )}
                        {!!periodForm.studentIds?.length && (
                          <span className="text-xs font-bold text-white bg-emerald-500 rounded-full px-2 py-0.5">
                            {periodForm.studentIds.length} selected
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="px-4 py-2.5 border-b border-slate-200 bg-white shrink-0">
                      <div className="relative">
                        <input
                          type="text"
                          value={studentSearchTerm}
                          onChange={(e) => setStudentSearchTerm(e.target.value)}
                          placeholder="Search by name or admission number"
                          disabled={!periodForm.subjectId}
                          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300/60 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-700 disabled:bg-slate-100 disabled:text-slate-400"
                        />
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto divide-y divide-slate-100 min-h-[240px]">
                      {!periodForm.subjectId ? (
                        <div className="flex flex-col items-center justify-center h-full py-12 gap-2">
                          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-slate-400" />
                          </div>
                          <p className="text-sm text-slate-400 text-center">
                            Choose a course on the left to see enrolled
                            students.
                          </p>
                        </div>
                      ) : courseStudents.length === 0 &&
                        !courseStudentsLoading ? (
                        <div className="flex flex-col items-center justify-center h-full py-12 gap-2">
                          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-slate-400" />
                          </div>
                          <p className="text-sm text-slate-400 text-center">
                            No students are enrolled in this course yet.
                          </p>
                        </div>
                      ) : filteredCourseStudents.length === 0 &&
                        !courseStudentsLoading ? (
                        <div className="flex flex-col items-center justify-center h-full py-12 gap-2">
                          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                            <Search className="w-5 h-5 text-slate-400" />
                          </div>
                          <p className="text-sm text-slate-400 text-center">
                            No students match your search.
                          </p>
                        </div>
                      ) : (
                        filteredCourseStudents.map((student: any) => (
                          <label
                            key={student._id}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded accent-emerald-500 shrink-0"
                              checked={
                                periodForm.studentIds?.includes(student._id) ??
                                false
                              }
                              onChange={() => toggleStudentId(student._id)}
                            />
                            <span className="text-sm text-slate-700 font-medium flex-1 truncate">
                              {student.userInfo?.firstName}{" "}
                              {student.userInfo?.lastName}
                            </span>
                            <span className="text-xs text-slate-400 font-mono shrink-0">
                              {student.studentId}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end mt-6 pb-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2.5 border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all duration-300 hover:scale-105 shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    addPeriodMutation.isPending ||
                    updatePeriodMutation.isPending
                  }
                  className="px-8 py-2.5 bg-gradient-to-r from-emerald-300 to-emerald-400 text-white font-bold rounded-xl hover:shadow-xl hover:scale-105 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2"
                >
                  {addPeriodMutation.isPending ||
                  updatePeriodMutation.isPending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>{editingPeriod ? "Update" : "Add"} Period</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecretarySchedule;
